import ChevronDownIcon from 'mdi-react/ChevronDownIcon'
import CloseIcon from 'mdi-react/CloseIcon'
import React, { CSSProperties } from 'react'
import { MultiValueProps } from 'react-select'
import { ContainerProps } from 'react-select/src/components/containers'
import { OptionProps } from 'react-select/src/components/Option'
import { PlaceholderProps } from 'react-select/src/components/Placeholder'
import { SingleValueProps } from 'react-select/src/components/SingleValue'
import { makeStyles } from 'tss-react/mui'

import { Chip, CircularProgress, emphasize, IconButton, InputLabel, Theme, Typography, useTheme } from '@mui/material'

// We need to inject our custom theme into react-select
// Due to limitations in the theme object, it needs to be passed as any
export const useCustomSelectTheme = (): any => {
  const { spacing, ...theme } = useTheme()
  return (defaultTheme: any) => ({
    colors: {
      ...defaultTheme.colors,
      primary: theme.palette.primary.main,
      primary25: theme.palette.grey['200'],
      primary50: theme.palette.grey['200'],
      primary75: theme.palette.grey['200'],
    },
    ...theme,
    spacing,
  })
}

// Select styles are not composed with JSS due to the use of emotion in React-select
export const customStyles = {
  control: (provided: CSSProperties, { theme }: { theme: Theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: 0,
    borderBottom: '1px solid',
    height: theme.spacing(4),
    borderColor: theme.palette.grey['400'],
    cursor: 'pointer',
  }),
  indicatorsContainer: (provided: CSSProperties, { theme }: { theme: Theme }) => ({
    ...provided,
    ['& > div > svg']: {
      width: theme.spacing(3),
      height: theme.spacing(3),
    },
  }),
  indicatorSeparator: (provided: CSSProperties, { theme }: { theme: Theme }) => ({
    ...provided,
    margin: theme.spacing(0.5),
  }),
  menu: (provided: CSSProperties, { theme }: { theme: Theme }) => ({
    ...provided,
    border: 'none',
    borderRadius: 0,
    marginTop: theme.spacing(2),
    padding: 0,
    boxShadow: theme.shadows[2],
    zIndex: theme.zIndex.tooltip,
  }),
  menuList: (provided: CSSProperties) => ({
    ...provided,
    padding: 0,
  }),
  valueContainer: () => ({
    display: 'flex',
    flex: '1 1 auto',
    position: 'relative' as CSSProperties['position'],
    padding: 0,
  }),
}

function InfoMessage({ children }: any) {
  const { classes } = useNoOptionsMessageStyles()

  return (
    <div className={classes.noOptionsContainer}>
      <Typography variant="body1">{children}</Typography>
    </div>
  )
}

const useNoOptionsMessageStyles = makeStyles()(({ spacing }: Theme) => ({
  noOptionsContainer: {
    padding: spacing(2),
  },
}))

function SelectContainer({ selectProps, innerProps, children }: ContainerProps<any, false>) {
  // Injected props
  const { label, error, isDisabled, required, className } = selectProps

  const { classes, cx } = useContainerStyles()

  return (
    <div className={cx(className, { [classes.disabled]: isDisabled })} {...innerProps}>
      {label && (
        <InputLabel error={!!error} required={required} shrink>
          {label}
        </InputLabel>
      )}
      <div className={classes.container}>{children}</div>
    </div>
  )
}

const useContainerStyles = makeStyles()({
  disabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  container: {
    position: 'relative',
  },
})

function Input({ selectProps, theme, getStyles, cx, isHidden, isDisabled, innerRef, ...props }: any) {
  const { classes } = useInputStyles()

  return <input className={classes.input} {...getFilteredInputProps(props)} ref={innerRef} disabled={isDisabled} />
}

function getFilteredInputProps({
  clearValue,
  form,
  getValue,
  hasValue,
  isMulti,
  isRtl,
  options,
  selectOption,
  setValue,
  ...filteredProps
}: any): any {
  return filteredProps
}

const useInputStyles = makeStyles()(({ typography }) => ({
  input: {
    ...(typography.body1 as any),
    background: 'transparent',
    width: '100%',
    outline: 'none',
    border: 'none',
    ['&:disabled']: {
      background: 'none',
      cursor: 'not-allowed',
    },
  },
}))

function MultiValue({ data: { isValid }, children, isFocused, removeProps }: MultiValueProps<any>) {
  const { classes, cx } = useMultiValueStyles()

  return (
    <Chip
      color="primary"
      tabIndex={-1}
      label={children}
      className={cx(classes.chip, {
        [classes.chipFocused]: isFocused,
        [classes.disabledChip]: isValid === false,
      })}
      onDelete={removeProps.onClick}
    />
  )
}

const useMultiValueStyles = makeStyles()(({ spacing, palette }: Theme) => ({
  chip: {
    marginRight: spacing(0.5),
    marginBottom: spacing(0.5),
  },
  chipFocused: {
    backgroundColor: emphasize(palette.mode === 'light' ? palette.grey[300] : palette.grey[700], 0.08),
  },
  disabledChip: {
    backgroundColor: palette.error.main,
  },
}))

function SingleValue({ children, selectProps }: SingleValueProps<any>) {
  const { classes } = useSingleValueStyles()
  // Injected props
  const { error } = selectProps
  return (
    <div className={classes.singleValueContainer}>
      <Typography variant="body1" component="span" color={error ? 'error' : 'textPrimary'}>
        {children}
      </Typography>
    </div>
  )
}

const useSingleValueStyles = makeStyles()({
  singleValueContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
  },
})

function Placeholder({ innerProps, selectProps, children }: PlaceholderProps<any, false>) {
  // Injected props
  const { error } = selectProps
  return (
    <Typography {...innerProps} color={error ? 'error' : 'inherit'}>
      {children}
    </Typography>
  )
}

function Option({ innerProps, children, isSelected, isFocused }: OptionProps<any, false>) {
  const { classes, cx } = useOptionStyles()
  const containerProps = {
    id: innerProps.id,
    onClick: innerProps.onClick,
    tabIndex: innerProps.tabIndex,
  }
  return (
    <div
      className={cx(classes.option, {
        [classes.optionSelected]: isSelected,
        [classes.optionFocused]: isFocused,
      })}
      {...containerProps}
    >
      <Typography component="div" variant="body1" color="inherit">
        {children}
      </Typography>
    </div>
  )
}

const useOptionStyles = makeStyles()(({ spacing, palette }: Theme) => ({
  option: {
    padding: spacing(2),
    userSelect: 'none',
    cursor: 'pointer',
    ['&:hover']: {
      backgroundColor: palette.primary.main,
      color: 'white',
    },
  },
  optionSelected: {
    backgroundColor: palette.grey['200'],
  },
  optionFocused: {
    backgroundColor: palette.primary.main,
    color: 'white',
  },
}))

function LoadingIndicator() {
  const { classes } = useLoadingIndicatorStyles()
  return <CircularProgress className={classes.spinner} color="secondary" />
}

const useLoadingIndicatorStyles = makeStyles()(({ spacing }: Theme) => ({
  spinner: {
    marginRight: spacing(),
    width: spacing(2.5),
    height: spacing(2.5),
    opacity: 0.5,
  },
}))

function ClearIndicator({ innerProps }: any) {
  const { classes } = useClearIndicatorStyles()
  return (
    <IconButton {...innerProps} className={classes.clearIcon} color="inherit" size="small" edge={'end'}>
      <CloseIcon />
    </IconButton>
  )
}

const useClearIndicatorStyles = makeStyles()({
  clearIcon: {
    opacity: 0.5,
  },
})

function DropdownIndicator({ innerProps }: any) {
  const { classes } = useDropdownIndicatorStyles()
  return (
    <IconButton {...innerProps} className={classes.dropdown} color="inherit" size="small" edge={'end'}>
      <ChevronDownIcon />
    </IconButton>
  )
}

const useDropdownIndicatorStyles = makeStyles()(({ palette, transitions }: Theme) => ({
  dropdown: {
    display: 'flex',
    alignItems: 'center',
    color: palette.grey['300'],
    transition: transitions.create('color'),
    ['&:hover']: {
      color: palette.grey['600'],
    },
  },
}))

export type SelectStyleProps = {
  gutterBottom: boolean
  helperTextVisible: boolean
}

export const defaultSelectComponents = {
  NoOptionsMessage: InfoMessage,
  LoadingMessage: InfoMessage,
  Input,
  Placeholder,
  SelectContainer,
  SingleValue,
  Option,
  LoadingIndicator,
  ClearIndicator,
  DropdownIndicator,
  MultiValue,
}
